import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ArrowLeft, Search, FileText, Trash2, Pencil, Plus, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';

const LIMIT = 20;

const AdminContentLibraryPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [contentType, setContentType] = useState('all');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteSlug, setDeleteSlug] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getPageContentList({
        skip,
        limit: LIMIT,
        search,
        contentType: contentType === 'all' ? undefined : contentType,
        status: status === 'all' ? undefined : status,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching content library:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [skip, search, contentType, status, dateFrom, dateTo]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSkip(0);
    setSearch(searchInput.trim());
  };

  const handleDelete = async () => {
    if (!deleteSlug) return;
    setDeleting(true);
    try {
      await adminApi.deletePageContent(deleteSlug);
      setItems((prev) => prev.filter((it) => it.slug !== deleteSlug));
      setTotal((prev) => prev - 1);
      toast.success('Content deleted');
    } catch (err) {
      console.error('Error deleting content:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete content');
    } finally {
      setDeleting(false);
      setDeleteSlug(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + LIMIT, total);

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-content-library-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Content Library</h1>
            <p className="text-muted-foreground text-sm">Articles and Tool/Simulator User Guides</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2" data-testid="create-content-btn">
                <Plus className="h-4 w-4" /> Create New <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/admin/content/new/article')} data-testid="create-new-article">
                New Article
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/admin/content/new/how_it_works')} data-testid="create-new-how-it-works">
                New How It Works Topic
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-6">
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
            <Input
              placeholder="Search by title..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-[220px]"
              data-testid="content-search-input"
            />
            <Button type="submit" variant="outline" size="icon" aria-label="Search">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          <Select value={contentType} onValueChange={(v) => { setSkip(0); setContentType(v); }}>
            <SelectTrigger className="w-[140px]" data-testid="filter-type-select">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="article">Article</SelectItem>
              <SelectItem value="guide">Guide</SelectItem>
              <SelectItem value="how_it_works">How It Works</SelectItem>
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={(v) => { setSkip(0); setStatus(v); }}>
            <SelectTrigger className="w-[140px]" data-testid="filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="PUBLISHED">Published</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">From</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setSkip(0); setDateFrom(e.target.value); }}
              className="w-[150px]"
              data-testid="filter-date-from"
            />
            <label className="text-xs text-muted-foreground">To</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setSkip(0); setDateTo(e.target.value); }}
              className="w-[150px]"
              data-testid="filter-date-to"
            />
          </div>

          <span className="text-sm text-muted-foreground ml-auto">
            {total === 0 ? '0 items' : `${from}-${to} of ${total}`}
          </span>
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-content-message">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No content matches these filters.</p>
              </div>
            ) : (
              <Table data-testid="content-library-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.page_content_id} data-testid={`content-row-${item.slug}`}>
                      <TableCell className="font-medium max-w-[260px] truncate">{item.title}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                          {item.content_type}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground">
                        {item.category || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={item.status === 'PUBLISHED' ? 'border-emerald-500/50 text-emerald-700' : 'border-amber-500/50 text-amber-700'}
                        >
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(item.updated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/admin/content/${item.slug}`)}
                          aria-label="Edit"
                          data-testid={`edit-content-${item.slug}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteSlug(item.slug)}
                          aria-label="Delete"
                          data-testid={`delete-content-${item.slug}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {total > LIMIT && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
              data-testid="content-prev-page-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + LIMIT >= total}
              onClick={() => setSkip((s) => s + LIMIT)}
              data-testid="content-next-page-btn"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteSlug} onOpenChange={() => setDeleteSlug(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Delete Content?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the page from the site (if published, it will 404). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-content-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminContentLibraryPage;
